import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import clinicService from '../../../services/clinic.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import PmsUpdateHistoryListItem from './subparts/PmsUpdateHistoryListItem';
import PmsUpdateHistoryDetailsModal from './subparts/PmsUpdateHistoryDetailsModal';
import PmsUpdateHistorySkeleton from './subparts/PmsUpdateHistorySkeleton';
import './PmsUpdateHistory.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const PmsUpdateHistory = () => {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [latestSync, setLatestSync] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchHistory = useCallback(
        async (page, size) => {
            if (!clinicId) {
                return;
            }

            setLoading(true);

            try {
                const res = await clinicService.getUpdateHistory(clinicId, page, size);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);

                if ((data.number ?? page) === 0 && content.length > 0) {
                    setLatestSync(content[0].createdAt);
                }
            } catch (err) {
                console.error('Failed to load PMS update history', err);
                toast.error('Failed to load PMS update history');
            } finally {
                setLoading(false);
            }
        },
        [clinicId]
    );

    useEffect(() => {
        fetchHistory(pageNumber, pageSize);
    }, [fetchHistory, pageNumber, pageSize]);

    const pagePmsTypes = useMemo(
        () => [...new Set(records.map((item) => item.pmsType).filter(Boolean))],
        [records]
    );

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleViewLogs = (item) => {
        setSelectedRecord(item);
    };

    const handleCloseModal = () => {
        setSelectedRecord(null);
    };

    const description = totalElements
        ? `Practice Management System sync logs · ${totalElements} total record${totalElements === 1 ? '' : 's'}`
        : 'Practice Management System sync logs';

    return (
        <div className="ecx-page pms-update-history-page ecx-page--full-width">
            <SubHeader
                title="PMS Update History"
                description={description}
                icon="bi-arrow-repeat"
                containerClass="w-100 px-4"
            >
                <div className="pms-update-history-latest-sync text-end">
                    <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                        <Link
                            to="/help?category=pms-update-history"
                            className="btn btn-sm btn-outline-primary rounded-pill"
                        >
                            <i className="bi bi-question-circle me-1"></i>
                            Help
                        </Link>
                    </div>
                    <div className="small text-muted mb-1">Latest sync</div>
                    <div className="fw-semibold text-dark">{latestSync || '—'}</div>
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel">
                    <div className="ecx-page-panel__header">
                        <div className="pms-update-history-toolbar">
                            <div className="pms-update-history-toolbar__meta">
                                <h5 className="mb-0 fw-semibold text-dark">Sync History</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    Page {totalPages === 0 ? 0 : pageNumber + 1} of {Math.max(totalPages, 1)}
                                </span>
                                {pagePmsTypes.length > 0 && (
                                    <span className="ecx-page-chip">
                                        <i className="bi bi-laptop"></i>
                                        {pagePmsTypes.join(', ')}
                                    </span>
                                )}
                            </div>

                            <div className="pms-update-history-toolbar__controls">
                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column">
                        {loading ? (
                            <PmsUpdateHistorySkeleton count={pageSize > 5 ? 5 : pageSize} />
                        ) : records.length > 0 ? (
                            <>
                                <div className="pms-update-history-list flex-grow-1">
                                    {records.map((item) => (
                                        <PmsUpdateHistoryListItem
                                            key={item.id}
                                            item={item}
                                            onViewLogs={handleViewLogs}
                                        />
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top pt-3 mt-3">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-arrow-repeat"></i>
                                </div>
                                <h5 className="text-dark fw-semibold mb-2">No PMS sync history yet</h5>
                                <p className="mb-0">
                                    Updates from your Practice Management System will appear here once syncing begins.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <PmsUpdateHistoryDetailsModal
                show={Boolean(selectedRecord)}
                onClose={handleCloseModal}
                item={selectedRecord}
            />
        </div>
    );
};

export default PmsUpdateHistory;
